<template>
    <div class="pt-[2px] space-y-4">
        <UTabs v-model="selectedTab" :items="tabs" :ui="tabsUi">
            <template #default="{ item }">
                <span>{{ item.label }}</span>
            </template>
        </UTabs>

        <div v-if="selectedTab === 0">
            <div class="mb-4 flex items-center justify-between space-x-2">
                <h1 class="text-lg font-semibold text-gray-700">Posts</h1>
                <div class="flex items-center space-x-2">
                    <UInput v-model="postSearch" type="text" placeholder="Search..." icon="i-ph-magnifying-glass-duotone" />
                    <UButton color="black" icon="i-ph-plus-bold" @click="editPost()">
                        Add Post
                    </UButton>
                </div>
            </div>

            <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                <UTable
                    :rows="filteredPosts"
                    :columns="postColumns"
                    :loading="loading"
                    :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                    :empty-state="{ icon: 'i-ph-map-pin-duotone', label: 'No field posts.' }"
                    :ui="{ td: { padding: 'py-3' }, tr: { base: 'cursor-pointer hover:bg-gray-50' } }"
                    @select="editPost"
                >
                    <template #code-data="{ row }">
                        <span class="font-mono">{{ row.code }}</span>
                    </template>
                    <template #areas-data="{ row }">
                        <span v-if="row.areas.length">{{ row.areas.join(', ') }}</span>
                        <span v-else class="text-gray-300">–</span>
                    </template>
                    <template #storages-data="{ row }">
                        <span v-if="activeStorageNames(row)">{{ activeStorageNames(row) }}</span>
                        <span v-else class="text-gray-300">–</span>
                    </template>
                    <template #is_active-data="{ row }">
                        <BadgeActiveStatus :active="row.is_active" />
                    </template>
                    <template #actions-data="{ row }">
                        <div @click.stop>
                            <UDropdown :items="postActions(row)">
                                <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                            </UDropdown>
                        </div>
                    </template>
                </UTable>
            </div>
        </div>

        <div v-else>
            <div class="mb-4 flex items-center justify-between space-x-2">
                <h1 class="text-lg font-semibold text-gray-700">Item Catalog</h1>
                <div class="flex items-center space-x-2">
                    <UInput v-model="itemSearch" type="text" placeholder="Search..." icon="i-ph-magnifying-glass-duotone" />
                    <USelectMenu
                        v-model="itemStatus"
                        trailing
                        class="w-44"
                        :ui="{
                            icon: { base: '!size-4', trailing: { pointer: 'pointer-events-auto', base: '!size-4' } }
                        }"
                        :options="statusOptions"
                    >
                        <template #label>
                            <span class="text-gray-400">Status:</span>
                            <span class="truncate font-medium">{{ itemStatus.name }}</span>
                        </template>
                        <template #option="{ option }">
                            <span class="truncate">{{ option.name }}</span>
                        </template>
                        <template v-if="itemStatus.value !== 'active'" #trailing>
                            <button type="button" class="flex items-center justify-center" @click.stop="itemStatus = statusOptions[0]">
                                <UIcon name="i-ph-x-bold" class="size-3" />
                            </button>
                        </template>
                    </USelectMenu>
                    <UButton color="black" icon="i-ph-plus-bold" @click="editItem()">
                        Add Item
                    </UButton>
                </div>
            </div>

            <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                <UTable
                    :rows="filteredItems"
                    :columns="itemColumns"
                    :loading="loading"
                    :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                    :empty-state="{ icon: 'i-ph-package-duotone', label: 'No items.' }"
                    :ui="{ td: { padding: 'py-3' }, tr: { base: 'cursor-pointer hover:bg-gray-50' } }"
                    @select="editItem"
                >
                    <template #specification-data="{ row }">
                        <span v-if="row.specification">{{ row.specification }}</span>
                        <span v-else class="text-gray-300">–</span>
                    </template>
                    <template #kind-data="{ row }">
                        <UBadge :color="row.kind === InventoryItemKind.medication ? 'blue' : 'gray'" variant="subtle" size="sm"
                            :label="row.kind === InventoryItemKind.medication ? 'Medication' : 'Consumable'" />
                    </template>
                    <template #is_archived-data="{ row }">
                        <BadgeActiveStatus :active="!row.is_archived" inactive-label="Archived" />
                    </template>
                    <template #actions-data="{ row }">
                        <div @click.stop>
                            <UDropdown :items="itemActions(row)">
                                <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                            </UDropdown>
                        </div>
                    </template>
                </UTable>
            </div>
        </div>

        <OverlayFieldPostEditPost v-model:open="overlays.post" :post="selectedPost" @reload="fetchAll" />
        <OverlayFieldPostEditItem v-model:open="overlays.item" :item="selectedItem" @reload="fetchAll" />
    </div>
</template>

<script setup lang="ts">
import { FIELD_POST_MANAGE_ROLES, InventoryItemKind, type FieldPost, type InventoryItem } from '~/types/fieldPost'

definePageMeta({ layout: 'default', middleware: 'auth', roles: FIELD_POST_MANAGE_ROLES })
useHead({ title: 'Settings – Field Posts' })

const client = useSupabaseClient()
const toast = useToast()

const tabs = [{ label: 'Posts' }, { label: 'Item Catalog' }]

const tabsUi = {
    list: {
        background: 'bg-gray-100',
        marker: { background: 'bg-white shadow-sm' },
    },
}

const statusOptions = [
    { value: 'active', name: 'Active' },
    { value: 'archived', name: 'Archived' },
    { value: 'all', name: 'All' },
]

const selectedTab = ref(0)
const loading = ref(false)
const posts = ref<FieldPost[]>([])
const items = ref<InventoryItem[]>([])
const postSearch = ref('')
const itemSearch = ref('')
const itemStatus = ref(statusOptions[0])
const overlays = reactive({ post: false, item: false })
const selectedPost = ref<FieldPost>()
const selectedItem = ref<InventoryItem>()

const postColumns = [
    { key: 'name', label: 'Name' },
    { key: 'code', label: 'Code' },
    { key: 'areas', label: 'Areas' },
    { key: 'storages', label: 'Storage Spots' },
    { key: 'is_active', label: 'Status' },
    { key: 'actions', class: 'w-px' },
]

const itemColumns = [
    { key: 'name', label: 'Name' },
    { key: 'specification', label: 'Specification' },
    { key: 'unit', label: 'Unit' },
    { key: 'kind', label: 'Kind' },
    { key: 'is_archived', label: 'Status' },
    { key: 'actions', class: 'w-px' },
]

const filteredPosts = computed(() => {
    const q = postSearch.value.trim().toLowerCase()
    return posts.value.filter(p => !q || `${p.name} ${p.code}`.toLowerCase().includes(q))
})

const filteredItems = computed(() => {
    const q = itemSearch.value.trim().toLowerCase()
    const status = itemStatus.value.value
    return items.value.filter(i =>
        (status === 'all' || (status === 'archived') === i.is_archived)
        && (!q || `${i.name} ${i.specification ?? ''}`.toLowerCase().includes(q)))
})

const postActions = (post: FieldPost) => [[
    { label: 'Edit', icon: 'i-ph-pencil-fill', click: () => editPost(post) },
]]

const itemActions = (item: InventoryItem) => [[
    { label: 'Edit', icon: 'i-ph-pencil-fill', click: () => editItem(item) },
    item.is_archived
        ? { label: 'Unarchive', icon: 'i-ph-arrow-counter-clockwise-fill', click: () => setItemArchived(item, false) }
        : { label: 'Archive', icon: 'i-ph-archive-fill', click: () => setItemArchived(item, true) },
]]

function activeStorageNames(post: FieldPost) {
    return [...(post.storages ?? [])]
        .filter(s => !s.is_archived)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(s => s.name)
        .join(', ')
}

function editPost(post?: FieldPost) {
    selectedPost.value = post
    overlays.post = true
}

function editItem(item?: InventoryItem) {
    selectedItem.value = item
    overlays.item = true
}

async function setItemArchived(item: InventoryItem, isArchived: boolean) {
    const { error } = await client.from('inventory_items').update({ is_archived: isArchived } as never).eq('id', item.id)
    if (error) {
        toast.add({ title: 'Could not update the item.', description: error.message, color: 'red', icon: 'i-ph-x-circle-duotone' })
        return
    }
    toast.add({ title: isArchived ? 'Item archived.' : 'Item unarchived.', color: 'green', icon: 'i-ph-check-circle-duotone' })
    fetchAll()
}

onMounted(() => fetchAll())

async function fetchAll() {
    loading.value = true
    const [postsRes, itemsRes] = await Promise.all([
        client.from('field_posts').select('*, storages:field_post_storages (*)').order('name'),
        client.from('inventory_items').select('*').order('name'),
    ])
    posts.value = (postsRes.data ?? []) as FieldPost[]
    items.value = (itemsRes.data ?? []) as InventoryItem[]
    loading.value = false
}
</script>
